import React, { useState, useEffect, useRef, useCallback } from "react";
import checkgif from "../../assets/gif/loading-gif.gif";
import close from "../../assets/icons/close.svg";
import { useWebSocket } from "../../Utils/WebSocketHooks";
import Notifier from "../../Utils/notify";
import { Postdata } from "../../API/GlobalApi";

const TIMEOUT_MS = 10 * 60 * 1000;
const AUTO_CLOSE_AFTER_RESOLVE_MS = 5 * 1000;

// Build the WS URL once. Prefer REACT_APP_API_WSS_URL if set; otherwise derive
// it from REACT_APP_API_BASE_URL by swapping the scheme and appending the
// finance whatsapp-webhooks consumer path that the existing modal also uses.
function buildWsUrl() {
  const explicit = process.env.REACT_APP_API_WSS_URL;
  if (explicit) return explicit;
  const base = process.env.REACT_APP_API_BASE_URL || "";
  if (!base) return "";
  const wsBase = base
    .replace(/^https:\/\//, "wss://")
    .replace(/^http:\/\//, "ws://")
    .replace(/\/$/, "");
  return `${wsBase}/ws/finance/whatsapp-webhooks/`;
}

export const WaitApprovalForEmployeeChange = ({
  isOpen,
  requestId,
  onApproved,
  onRejectedOrCancelled,
  onClose,
}) => {
  const [status, setStatus] = useState({ sent: false, delivered: false, read: false });
  const [finalStatus, setFinalStatus] = useState(null);

  // Refs to keep WS callback identity stable across renders, so the
  // useWebSocket hook's `connect` doesn't churn and re-open every render.
  const requestIdRef = useRef(requestId);
  const onApprovedRef = useRef(onApproved);
  const onRejectedRef = useRef(onRejectedOrCancelled);
  const cancelledRef = useRef(false);
  const timeoutHandleRef = useRef(null);

  useEffect(() => { requestIdRef.current = requestId; }, [requestId]);
  useEffect(() => { onApprovedRef.current = onApproved; }, [onApproved]);
  useEffect(() => { onRejectedRef.current = onRejectedOrCancelled; }, [onRejectedOrCancelled]);

  const wsUrl = useRef(buildWsUrl()).current;

  const handleMessage = useCallback((data) => {
    if (!data || data.type !== "whatsapp_webhook") return;
    const payload = data.data || {};
    const currentId = requestIdRef.current;
    if (payload.request_id && currentId && payload.request_id !== currentId) return;
    if (payload.kind && payload.kind !== "employee_approval") return;

    const waStatus = (payload.whatsapp_status || payload.status_text || "").toUpperCase();
    if (waStatus === "SENT") {
      setStatus((p) => ({ ...p, sent: true }));
    } else if (waStatus === "DELIVERED") {
      setStatus((p) => ({ ...p, sent: true, delivered: true }));
    } else if (waStatus === "READ") {
      setStatus((p) => ({ ...p, sent: true, delivered: true, read: true }));
    }

    const backendStatus = (payload.status || "").toLowerCase();
    if (backendStatus === "approved") {
      setFinalStatus("approved");
      Notifier.success("Employee change approved.");
      onApprovedRef.current?.(payload);
    } else if (
      backendStatus === "rejected" ||
      backendStatus === "expired" ||
      backendStatus === "cancelled" ||
      backendStatus === "failed"
    ) {
      setFinalStatus(backendStatus);
      if (backendStatus === "rejected") Notifier.error("Employee change was rejected by the approver.");
      else if (backendStatus === "expired") Notifier.error("Approval request timed out. Please try again.");
      else if (backendStatus === "failed") Notifier.error("Approval failed to apply — see the change request log.");
      onRejectedRef.current?.(backendStatus, payload);
    }
  }, []);

  const { readyState, connect, disconnect } = useWebSocket(wsUrl, {
    reconnect: true,
    reconnectInterval: 2000,
    onMessage: handleMessage,
    onOpen: () => console.log("✅ employee-approval WS connected"),
    onClose: () => console.log("🔌 employee-approval WS disconnected"),
    onError: (err) => console.warn("⚠️ employee-approval WS error", err),
  });

  // Open the WS only when isOpen flips true; close only on unmount or when isOpen→false.
  // NOTE: intentionally omit connect/disconnect from deps so callback identity
  // changes don't bounce the socket.
  useEffect(() => {
    if (!isOpen) return undefined;
    connect();
    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // 10-minute timeout → cancel + close
  useEffect(() => {
    if (!isOpen || finalStatus) return undefined;
    timeoutHandleRef.current = setTimeout(() => {
      cancelOnServer("timeout");
      Notifier.error("Approval request timed out after 10 minutes.");
      setFinalStatus("expired");
      onRejectedRef.current?.("expired");
    }, TIMEOUT_MS);
    return () => {
      if (timeoutHandleRef.current) {
        clearTimeout(timeoutHandleRef.current);
        timeoutHandleRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, finalStatus]);

  // Auto-close shortly after resolution so the user can read the outcome.
  useEffect(() => {
    if (!finalStatus) return undefined;
    const t = setTimeout(() => onClose?.(), AUTO_CLOSE_AFTER_RESOLVE_MS);
    return () => clearTimeout(t);
  }, [finalStatus, onClose]);

  const cancelOnServer = async (reason) => {
    const id = requestIdRef.current;
    if (!id || cancelledRef.current) return;
    cancelledRef.current = true;
    try {
      await Postdata(`staff/employee-change-requests/${id}/cancel/`, {});
    } catch (err) {
      console.warn("Cancel call failed", reason, err);
    }
  };

  const handleManualClose = async () => {
    if (!finalStatus) {
      await cancelOnServer("manual_close");
      onRejectedRef.current?.("cancelled");
    }
    onClose?.();
  };

  if (!isOpen) return null;

  const Step = ({ active, label, rejected }) => (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div
        style={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          border: "2px solid #999",
          backgroundColor: rejected ? "red" : active ? "#4caf50" : "transparent",
          marginBottom: 5,
        }}
      />
      <span style={{ fontSize: 12, color: rejected ? "red" : active ? "#000" : "#999" }}>{label}</span>
    </div>
  );

  const isFailureFinal = ["rejected", "expired", "cancelled", "failed"].includes(finalStatus);

  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        style={{ position: "relative", width: "80%", maxWidth: 500, borderRadius: 12, padding: 20 }}
      >
        <button
          onClick={handleManualClose}
          style={{ background: "none", border: "none", cursor: "pointer", position: "absolute", top: 10, right: 10 }}
          aria-label="Close"
        >
          <img src={close} alt="close" width="18" />
        </button>

        <div style={{ textAlign: "center" }}>
          <img src={checkgif} alt="waiting" style={{ width: 200, height: 200 }} />

          <div style={{ display: "flex", justifyContent: "space-around", margin: "20px 0" }}>
            <Step active={status.sent} label="Sent" />
            <Step active={status.delivered} label="Delivered" />
            <Step active={status.read} label="Read" />
            <Step
              active={finalStatus === "approved" || isFailureFinal}
              rejected={isFailureFinal}
              label={
                finalStatus === "approved"
                  ? "Approved"
                  : finalStatus === "rejected"
                  ? "Rejected"
                  : finalStatus === "expired"
                  ? "Expired"
                  : finalStatus === "cancelled"
                  ? "Cancelled"
                  : finalStatus === "failed"
                  ? "Failed"
                  : "Approval"
              }
            />
          </div>

          {finalStatus ? (
            <p style={{ fontSize: 14, fontWeight: "bold", color: isFailureFinal ? "red" : "#7F56DA" }}>
              {finalStatus === "approved"
                ? "Approved. The change has been saved."
                : finalStatus === "rejected"
                ? "Rejected. The change was discarded."
                : finalStatus === "expired"
                ? "Timed out. The change was discarded."
                : finalStatus === "cancelled"
                ? "Cancelled. The change was discarded."
                : "Failed to apply. Inspect the change request."}
            </p>
          ) : (
            <p style={{ fontSize: 13, color: "#666" }}>
              Waiting for an approver to respond on WhatsApp. The request will time out in 10 minutes.
            </p>
          )}

          <p style={{ fontSize: 12, color: "#999" }}>
            WebSocket:{" "}
            {readyState === 0
              ? "Connecting..."
              : readyState === 1
              ? "Connected"
              : readyState === 2
              ? "Closing..."
              : readyState === 3
              ? "Closed"
              : "Not connected"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default WaitApprovalForEmployeeChange;
